// Oracle - Pricing Intelligence & Rate Actions
import { APIError, fetchAPI } from './client';
import { requireArrayKey, requireRecord } from './guards';
import type { Competitor, OracleBriefing, PricingContext, WeatherForecast } from '@/types';

// Generate unique idempotency key for mutations
function generateIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Oracle Chat
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
export interface ChatResponse {
  response: string;
  context?: Record<string, unknown>;
}

export async function sendOracleChat(
  property: string,
  message: string,
  history: ChatMessage[] = [],
): Promise<ChatResponse> {
  // Backend expects {messages: [...], property_name}
  // Convert message + history into messages array format
  const messages = [...history, { role: 'user' as const, content: message }];
  return fetchAPI<ChatResponse>('/oracle/chat', {
    method: 'POST',
    headers: {
      'X-Idempotency-Key': generateIdempotencyKey(),
    },
    body: JSON.stringify({ messages, property_name: property }),
  });
}

// Alias for GlobalChat and OracleBanner
export async function sendOracleMessage(
  messages: { role: string; content: string }[],
  property?: string,
): Promise<{ response: string; proposal_created?: boolean; proposal_id?: string }> {
  return fetchAPI('/oracle/chat', {
    method: 'POST',
    headers: {
      'X-Idempotency-Key': generateIdempotencyKey(),
    },
    body: JSON.stringify({ messages, property_name: property }),
  });
}

// Oracle Briefing
export async function fetchOracleBriefing(property?: string): Promise<OracleBriefing> {
  const params =
    property && property !== 'all' ? `?property_name=${encodeURIComponent(property)}` : '';
  return fetchAPI<OracleBriefing>(`/oracle/briefing${params}`);
}

// Properties
export async function fetchProperties(): Promise<{ properties: { id: string; name: string }[] }> {
  return fetchAPI('/properties');
}

// Weather - backend uses path param: /weather/{property}
// Backend returns: {date, high, low, conditions, precip_pct, glamping_score, price_multiplier}
interface BackendWeatherForecast {
  date: string;
  high: number;
  low: number;
  conditions: string;
  precip_pct: number;
  glamping_score?: number;
  price_multiplier?: number;
}

export async function fetchWeather(property: string, days: number = 7): Promise<WeatherForecast[]> {
  // URL encode property to handle special characters
  const forecasts = await fetchAPI<BackendWeatherForecast[]>(
    `/weather/${encodeURIComponent(property)}?days=${days}`,
  );
  // Transform to match frontend interface
  return (forecasts ?? []).map((f) => ({
    date: f.date,
    high: f.high,
    low: f.low,
    condition: f.conditions,
    precipitation_chance: f.precip_pct,
  }));
}

// Pricing Context
export async function fetchPricingContext(
  property: string,
  checkDate: string,
): Promise<PricingContext> {
  return fetchAPI<PricingContext>(
    `/pricing-context?property_name=${encodeURIComponent(property)}&check_date=${checkDate}`,
  );
}

interface CompetitorConfig {
  my_property_id: string;
  competitor_name: string;
  comp_type: string;
  learned_weight: number | null;
  unit_group_filters: string[] | null;
  source_type: string | null;
  target_url: string | null;
}

interface CompetitorsResponse {
  competitors: CompetitorConfig[];
}

export async function fetchCompetitors(
  property: string,
  _stayDate?: string,
): Promise<Competitor[]> {
  const params =
    property && property !== 'all' ? `?property_id=${encodeURIComponent(property)}` : '';
  const response = await fetchAPI<CompetitorsResponse>(`/settings/competitors${params}`);
  return (response.competitors ?? []).map((competitor) => ({
    name: competitor.competitor_name,
    weight: competitor.learned_weight ?? 1,
    source: competitor.source_type ?? undefined,
    type: competitor.comp_type ?? undefined,
  }));
}

export interface CompetitorStats {
  parity_avg: number | null;
  parity_floor: number | null;
  parity_ceiling: number | null;
  ceiling_avg: number | null;
  comp_count: number | null;
}

interface PricingContextResponse {
  targets?: Array<{ competitor_data?: CompetitorStats | null }>;
}

export async function fetchCompetitorStats(
  property: string,
  stayDate?: string,
): Promise<CompetitorStats | null> {
  if (!stayDate) return null;
  const params = new URLSearchParams({
    property_name: property,
    check_date: stayDate,
    days: '1',
  });
  const response = await fetchAPI<PricingContextResponse>(`/pricing-context?${params}`);
  return response.targets?.[0]?.competitor_data ?? null;
}

// Room Types - backend returns {room_types: [...]}
interface RoomTypesResponse {
  room_types: Array<{ room_type_key: string; room_type_name: string; display_order: number }>;
}

export async function fetchRoomTypes(
  property: string,
): Promise<Array<{ room_type_key: string; room_type_name: string; display_order: number }>> {
  const response = await fetchAPI<RoomTypesResponse>(
    `/room-types?property_name=${encodeURIComponent(property)}`,
  );
  return response.room_types ?? [];
}

// LOS Restrictions - backend returns {restrictions: [...], property}
// Backend fields: stay_date, min_los, max_los, close_to_arrival, close_to_departure, reason, created_at
interface BackendLOSRestriction {
  stay_date: string;
  start_date?: string;
  end_date?: string;
  room_type_key?: string | null;
  category_id?: string | null;
  min_los: number | null;
  max_los: number | null;
  close_to_arrival?: boolean;
  close_to_departure?: boolean;
  reason?: string | null;
  created_at?: string;
}

interface LOSRestrictionsResponse {
  restrictions: BackendLOSRestriction[];
  property: string;
}

export interface LOSRestriction {
  stayDate: string;
  roomTypeKey: string | null;
  categoryId: string | null;
  minLos: number | null;
  maxLos: number | null;
  closeToArrival: boolean | null;
  closeToDeparture: boolean | null;
  notes: string | null;
  createdAt: string | null;
}

function formatLosDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function expandLosRestrictionDates(restriction: BackendLOSRestriction): string[] {
  const start = restriction.start_date ?? restriction.stay_date;
  const end = restriction.end_date ?? restriction.stay_date ?? start;
  if (!start || !end) return [];

  const startDate = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
    return restriction.stay_date ? [restriction.stay_date] : [];
  }

  const dates: string[] = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    dates.push(formatLosDate(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

function mergeLOSRestriction(
  existing: LOSRestriction | undefined,
  incoming: LOSRestriction,
): LOSRestriction {
  return {
    stayDate: incoming.stayDate,
    roomTypeKey: incoming.roomTypeKey ?? existing?.roomTypeKey ?? null,
    categoryId: incoming.categoryId ?? existing?.categoryId ?? null,
    minLos: incoming.minLos ?? existing?.minLos ?? null,
    maxLos: incoming.maxLos ?? existing?.maxLos ?? null,
    closeToArrival: incoming.closeToArrival ?? existing?.closeToArrival ?? null,
    closeToDeparture: incoming.closeToDeparture ?? existing?.closeToDeparture ?? null,
    notes: incoming.notes ?? existing?.notes ?? null,
    createdAt: incoming.createdAt ?? existing?.createdAt ?? null,
  };
}

export async function fetchLOSRestrictions(
  property: string,
  daysAhead: number = 90,
): Promise<LOSRestriction[]> {
  const query = new URLSearchParams({
    property_name: property,
    days_ahead: String(daysAhead),
  }).toString();
  const endpoints = [`/los-restrictions?${query}`, `/settings/los-restrictions?${query}`];

  const toRestrictions = (response: LOSRestrictionsResponse): LOSRestriction[] => {
    const restrictionsByDate = new Map<string, LOSRestriction>();

    for (const restriction of response.restrictions ?? []) {
      const expandedDates = expandLosRestrictionDates(restriction);
      for (const stayDate of expandedDates) {
        const roomTypeKey = restriction.room_type_key ?? null;
        const scopeKey = roomTypeKey ? `${roomTypeKey}-${stayDate}` : stayDate;
        const merged = mergeLOSRestriction(restrictionsByDate.get(scopeKey), {
          stayDate,
          roomTypeKey,
          categoryId: restriction.category_id ?? null,
          minLos: restriction.min_los,
          maxLos: restriction.max_los,
          closeToArrival: restriction.close_to_arrival ?? null,
          closeToDeparture: restriction.close_to_departure ?? null,
          notes: restriction.reason ?? null,
          createdAt: restriction.created_at ?? null,
        });
        restrictionsByDate.set(scopeKey, merged);
      }
    }

    return Array.from(restrictionsByDate.values()).sort((a, b) =>
      a.stayDate === b.stayDate
        ? (a.roomTypeKey ?? '').localeCompare(b.roomTypeKey ?? '')
        : a.stayDate.localeCompare(b.stayDate),
    );
  };

  for (let i = 0; i < endpoints.length; i += 1) {
    try {
      const response = await fetchAPI<LOSRestrictionsResponse>(endpoints[i]);
      return toRestrictions(response);
    } catch (error) {
      const isLastEndpoint = i === endpoints.length - 1;
      if (isLastEndpoint || !(error instanceof APIError) || error.status !== 404) {
        throw error;
      }
    }
  }

  return [];
}

export interface TraceRollupPoint {
  date: string;
  p95_ms: number | null;
  bytes_billed: number | null;
}

export interface TraceRollupLatencyPoint {
  date: string;
  p95_ms: number | null;
}

export interface TraceRollupBytesPoint {
  date: string;
  total_bytes_billed: number | null;
}

export interface TraceRollupResponse {
  days: number;
  services: string[];
  bq_p95_ms_by_day: TraceRollupLatencyPoint[];
  bytes_billed_by_day: TraceRollupBytesPoint[];
  series: TraceRollupPoint[];
}

export async function fetchOracleTraceRollups(
  days: number = 30,
  services?: string[],
): Promise<TraceRollupResponse> {
  const params = new URLSearchParams();
  params.set('days', String(days));
  if (services?.length) {
    params.set('services', services.join(','));
  }
  return fetchAPI<TraceRollupResponse>(`/oracle/trace-rollups?${params.toString()}`);
}

export interface TraceCorrelationPoint {
  date: string;
  total_bytes_billed: number;
  p95_ms: number | null;
}

export interface TraceCorrelationResponse {
  days: number;
  services: string[];
  pearson_r: number | null;
  n: number;
  series: TraceCorrelationPoint[];
  top_bytes_days: TraceCorrelationPoint[];
  top_latency_days: TraceCorrelationPoint[];
}

export async function fetchOracleTraceCorrelation(
  days: number = 30,
  services?: string[],
): Promise<TraceCorrelationResponse> {
  const params = new URLSearchParams();
  params.set('days', String(days));
  if (services?.length) {
    params.set('services', services.join(','));
  }
  return fetchAPI<TraceCorrelationResponse>(`/oracle/trace-correlation?${params.toString()}`);
}

// Room Mix
export interface RoomMixEntry {
  room_type_key: string;
  room_type_name: string;
  rooms_sold: number;
  total_rooms: number;
  occupancy_pct: number;
  revenue: number;
  adr: number;
  mix_pct: number;
}

export interface RoomMixResponse {
  property: string;
  period: { start_date: string; end_date: string };
  room_types: RoomMixEntry[];
  totals: { rooms_sold: number; revenue: number; adr: number };
}

export async function fetchRoomMix(
  property: string = 'soundry',
  startDate?: string,
  endDate?: string,
): Promise<RoomMixResponse> {
  const params = new URLSearchParams({ property_name: property });
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return fetchAPI(`/room-mix?${params}`);
}

// Market Position
export interface MarketPositionResponse {
  property: string;
  check_date: string;
  your_rate: number | null;
  your_floor: number | null;
  comp_avg: number | null;
  comp_floor: number | null;
  ceiling_avg: number | null;
  position_pct: number | null;
  position_label: 'BELOW' | 'ALIGNED' | 'ABOVE' | 'NO_DATA';
  day_type: string | null;
  comp_rate_authority?: 'qualified_normalized_quote' | 'redacted_unqualified';
  pricing_authority_status?: string;
  proposal_eligible?: boolean;
  runtime_eligibility?: boolean;
}

export async function fetchMarketPosition(
  property: string = 'soundry',
  checkDate?: string,
): Promise<MarketPositionResponse> {
  const params = new URLSearchParams({ property_name: property });
  if (checkDate) params.append('check_date', checkDate);
  return fetchAPI(`/market-position?${params}`);
}

// Oracle Priorities
export interface Priority {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  action_label: string;
  metadata?: Record<string, unknown>;
}

export interface PrioritiesResponse {
  property: string;
  generated_at: string;
  priorities: Priority[];
  stats: { critical: number; warning: number; info: number; total: number };
}

export async function fetchPriorities(property: string = 'soundry'): Promise<PrioritiesResponse> {
  return fetchAPI(`/oracle/priorities?property_name=${encodeURIComponent(property)}`);
}

// Cancellations
export interface CancellationsResponse {
  property: string;
  period: { start: string; end: string; days: number };
  cancellations: { count: number; room_nights: number; revenue_lost: number };
  new_bookings: { count: number; room_nights: number; revenue: number };
  net: { bookings: number; room_nights: number; revenue: number };
  cancel_rate: number;
}

export async function fetchCancellations(
  property?: string,
  days: number = 7,
): Promise<CancellationsResponse> {
  const params = new URLSearchParams({ days: days.toString() });
  if (property && property !== 'all') params.append('property_name', property);
  return fetchAPI(`/cancellations?${params}`);
}

import { authFetch, API_BASE } from './client';

// Store active abort controllers for streaming requests
const activeStreamControllers = new Map<string, AbortController>();

export async function sendOracleMessageStreaming(
  messages: { role: string; content: string }[],
  property: string | undefined,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
  streamId?: string,
): Promise<() => void> {
  // Abort any existing stream for this streamId
  const id = streamId || 'default';
  const existingController = activeStreamControllers.get(id);
  if (existingController) {
    existingController.abort();
    activeStreamControllers.delete(id);
  }

  // Create new abort controller
  const controller = new AbortController();
  activeStreamControllers.set(id, controller);

  // Validate property context - Oracle should only respond about the current property
  if (!property) {
    onError('Property context is required for Oracle chat');
    return () => {};
  }

  try {
    const response = await authFetch(`${API_BASE}/api/oracle/chat/v3/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Idempotency-Key': generateIdempotencyKey(),
      },
      body: JSON.stringify({
        messages,
        property_name: property,
        // Instruct backend to limit responses to property context
        context_restriction: 'property_only',
      }),
      signal: controller.signal,
      // Streaming responses regularly exceed the standard API timeout.
      // Rely on explicit aborts instead of a fixed deadline.
      timeout: 0,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      let message = `HTTP ${response.status}`;
      if (errorText) {
        try {
          const parsed = JSON.parse(errorText);
          message = parsed.detail || parsed.message || message;
        } catch {
          message = errorText;
        }
      }
      throw new Error(message);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    if (!reader) {
      throw new Error('No reader available');
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Check if aborted
      if (controller.signal.aborted) {
        reader.cancel();
        return () => {};
      }

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const event of events) {
        const dataLines: string[] = [];
        for (const line of event.split('\n')) {
          if (line.startsWith('data: ')) dataLines.push(line.slice(6));
        }
        const payload = dataLines.join('\n').trim();
        if (!payload) continue;

        try {
          const data = JSON.parse(payload);
          if (data.error) {
            activeStreamControllers.delete(id);
            onError(String(data.error));
            return () => {};
          }
          if (data.text) onChunk(String(data.text));
          if (data.done) {
            activeStreamControllers.delete(id);
            onDone();
            return () => {};
          }
        } catch {
          // Ignore non-JSON SSE payloads
        }
      }
    }

    activeStreamControllers.delete(id);
    onDone();
  } catch (err) {
    activeStreamControllers.delete(id);
    if (err instanceof Error && err.name === 'AbortError') {
      // Silently handle abort - this is intentional cancellation
      return () => {};
    }
    onError(err instanceof Error ? err.message : 'Unknown error');
  }

  // Return abort function
  return () => {
    controller.abort();
    activeStreamControllers.delete(id);
  };
}

// Helper to abort all active streams (useful on unmount)
export function abortAllOracleStreams(): void {
  activeStreamControllers.forEach((controller) => controller.abort());
  activeStreamControllers.clear();
}

// Oracle Recommendations
export interface OracleRecommendation {
  recommendation_id: string;
  property_id: string;
  recommendation_type: 'season_add' | 'boundary_update' | 'season_remove' | 'los_restriction';
  payload: Record<string, unknown>;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
  priority: 'critical' | 'high' | 'medium' | 'low';
  effective_start?: string;
  effective_end?: string;
  status: string;
  created_at: string;
  authority_status?: 'FIT_FOR_SHADOW_ONLY';
  approval_eligible?: boolean;
  mutation_authorized?: boolean;
  authority_blocker?: string;
}

export interface OracleRecommendationsResponse {
  recommendations: OracleRecommendation[];
  summary: {
    total: number;
    by_type: Record<string, number>;
  };
}

export async function fetchOracleRecommendations(
  property: string,
  status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending',
): Promise<OracleRecommendationsResponse> {
  const params = new URLSearchParams({
    property_name: property,
    status,
  });
  return fetchAPI<OracleRecommendationsResponse>(`/forecasting/oracle/recommendations?${params}`);
}

export async function reviewOracleRecommendation(
  recommendationId: string,
  status: 'approved' | 'rejected',
): Promise<{ status: string; recommendation_id: string; applied_hierarchy_id?: string | null }> {
  return fetchAPI(`/forecasting/oracle/recommendations/${recommendationId}/review`, {
    method: 'POST',
    headers: {
      'X-Idempotency-Key': generateIdempotencyKey(),
    },
    body: JSON.stringify({ status }),
  });
}

export async function requestAutogenerate(
  property: string,
): Promise<{ success: boolean; message?: string }> {
  return fetchAPI('/forecasting/oracle/autogenerate', {
    method: 'POST',
    headers: {
      'X-Idempotency-Key': generateIdempotencyKey(),
    },
    body: JSON.stringify({ property_name: property }),
  });
}

// Oracle Forecasts (12-month rolling)
export interface OracleForecast {
  property_id: string;
  room_type_id: string;
  forecast_month: string;
  predicted_revenue: number;
  predicted_occupancy: number;
  predicted_adr: number;
  predicted_room_nights: number;
  confidence: 'high' | 'medium' | 'low';
  factors: {
    seasonality?: number;
    pace?: number;
    events?: number;
    comp_position?: number;
  };
  reasoning: string;
  locked: boolean;
  locked_at?: string;
  version: number;
  updated_at: string;
}

export interface OracleForecastsResponse {
  forecasts: OracleForecast[];
  summary: {
    total_predicted_revenue: number;
    forecast_count: number;
    locked_count: number;
    unlocked_count: number;
  };
}

export async function fetchOracleForecasts(
  property: string,
  options?: { roomType?: string; includeLocked?: boolean },
): Promise<OracleForecastsResponse> {
  const params = new URLSearchParams({ property_name: property });
  if (options?.roomType) params.append('room_type', options.roomType);
  if (options?.includeLocked !== undefined)
    params.append('include_locked', String(options.includeLocked));
  const data = await fetchAPI<unknown>(`/forecasting/oracle/forecasts?${params}`);
  const record = requireRecord(data, 'oracle forecasts');
  requireArrayKey(record, 'forecasts', 'oracle forecasts');
  return record as unknown as OracleForecastsResponse;
}

export interface ForecastInterval {
  p10: number | null;
  p50: number | null;
  p90: number | null;
}

export interface ForecastV2Monthly {
  forecast_month: string;
  quality: 'high' | 'medium' | 'low' | 'degraded';
  quality_score: number;
  feature_completeness: number;
  source_mix: Record<string, number>;
  fallback_flags: string[];
  reasoning: string | null;
  intervals: {
    revenue: ForecastInterval;
    occupancy: ForecastInterval;
    adr: ForecastInterval;
    room_nights: ForecastInterval;
  };
}

export interface ForecastV2ForecastsResponse {
  property_id: string;
  run_id: string;
  model_version: string;
  as_of: string;
  quality: 'high' | 'medium' | 'low' | 'degraded';
  source_mix: Record<string, number>;
  freshness: Record<string, unknown>;
  accuracy_summary: Record<string, unknown>;
  gate_summary: Record<string, unknown>;
  forecasts: ForecastV2Monthly[];
  summary: {
    forecast_count: number;
  };
  status?: string;
}

export interface ForecastV2BaselineComparison {
  months_compared: number;
  comparison_horizon_days: number | null;
  baseline_revenue_wape: number | null;
  baseline_occupancy_mae_pp: number | null;
  v2_revenue_wape: number | null;
  v2_occupancy_mae_pp: number | null;
  revenue_wape_improvement_pct: number | null;
  occupancy_mae_improvement_pp: number | null;
}

export interface ForecastV2AccuracySummary {
  months_evaluated?: number;
  revenue_wape?: number | null;
  occupancy_mae_pp?: number | null;
  calibration_hit_rate?: number | null;
  first_operating_month?: string | null;
  first_mature_month?: string | null;
  pre_open_months_excluded?: number;
  immature_months_excluded?: number;
  baseline_comparison?: ForecastV2BaselineComparison;
  [key: string]: unknown;
}

export interface ForecastV2GateSummary {
  revenue_gate_passed?: boolean;
  occupancy_gate_passed?: boolean;
  calibration_gate_passed?: boolean;
  revenue_vs_baseline_passed?: boolean;
  occupancy_vs_baseline_passed?: boolean;
  all_passed?: boolean;
  [key: string]: unknown;
}

export interface ForecastV2AccuracyHistoryRow {
  forecast_month: string;
  as_of_date: string;
  horizon_days: number;
  horizon_bucket: string;
  quality: 'high' | 'medium' | 'low' | 'degraded';
  predicted_revenue: ForecastInterval;
  actual_revenue: number | null;
  revenue_abs_pct_error: number | null;
  predicted_occupancy: ForecastInterval;
  actual_occupancy: number | null;
  occupancy_abs_error: number | null;
  predicted_adr_p50: number | null;
  actual_adr: number | null;
  within_interval: boolean;
}

export interface ForecastV2AccuracyResponse {
  property_id: string;
  run_id: string;
  model_version: string;
  as_of: string;
  summary: ForecastV2AccuracySummary;
  history: ForecastV2AccuracyHistoryRow[];
  status?: string;
}

export interface ForecastV2HealthResponse {
  property_id: string;
  run_id: string;
  model_version: string;
  as_of: string;
  latest_run_status: string;
  quality: 'high' | 'medium' | 'low' | 'degraded';
  freshness: Record<string, unknown>;
  accuracy_summary: ForecastV2AccuracySummary;
  gate_summary: ForecastV2GateSummary;
  fallback_counts: Record<string, number>;
  warnings: string[];
  status?: string;
}

export async function fetchForecastV2Forecasts(
  property: string,
): Promise<ForecastV2ForecastsResponse> {
  const data = await fetchAPI<unknown>(
    `/forecasting/v2/forecasts?property_name=${encodeURIComponent(property)}`,
  );
  const record = requireRecord(data, 'forecast v2 forecasts');
  requireArrayKey(record, 'forecasts', 'forecast v2 forecasts');
  return record as unknown as ForecastV2ForecastsResponse;
}

export async function fetchForecastV2Accuracy(
  property: string,
): Promise<ForecastV2AccuracyResponse> {
  const data = await fetchAPI<unknown>(
    `/forecasting/v2/accuracy?property_name=${encodeURIComponent(property)}`,
  );
  const record = requireRecord(data, 'forecast v2 accuracy');
  requireArrayKey(record, 'history', 'forecast v2 accuracy');
  return record as unknown as ForecastV2AccuracyResponse;
}

export async function fetchForecastV2Health(property: string): Promise<ForecastV2HealthResponse> {
  const data = await fetchAPI<unknown>(
    `/forecasting/v2/health?property_name=${encodeURIComponent(property)}`,
  );
  const record = requireRecord(data, 'forecast v2 health');
  requireArrayKey(record, 'warnings', 'forecast v2 health');
  return record as unknown as ForecastV2HealthResponse;
}

// Oracle Retrospectives (monthly post-mortems)
export interface OracleRetrospective {
  retrospective_id: string;
  property_id: string;
  retrospective_month: string;
  narrative: string;
  learnings: Array<{ what_worked?: string; what_didnt?: string; insight?: string }>;
  forecast_accuracy: {
    predicted_revenue?: number;
    actual_revenue?: number;
    variance_pct?: number;
  };
  created_at: string;
}

export interface OracleRetrospectivesResponse {
  retrospectives: OracleRetrospective[];
  total: number;
}

export async function fetchOracleRetrospectives(
  property: string,
  limit: number = 12,
): Promise<OracleRetrospectivesResponse> {
  const params = new URLSearchParams({ property_name: property, limit: String(limit) });
  return fetchAPI<OracleRetrospectivesResponse>(`/forecasting/oracle/retrospectives?${params}`);
}

// Decision Evidence - M8: Recursive Learning Ready
export interface InputHealthEntry {
  quality: 'good' | 'partial' | 'stale' | 'none' | 'error';
  hours_stale: number;
}

export interface DecisionOutcome {
  booking_made: boolean | null;
  booking_rate: number | null;
  lead_time_days: number | null;
  channel: string | null;
  recorded_at: string | null;
}

export interface DecisionEvidence {
  decision_id: string;
  trace_id: string | null;
  run_id?: string | null;
  not_learnable: boolean;
  not_learnable_reason: string | null;
  input_health: Record<string, InputHealthEntry>;
  outcome: DecisionOutcome | null;
  error?: string;
}

export async function fetchDecisionEvidence(decisionId: string): Promise<DecisionEvidence> {
  return fetchAPI<DecisionEvidence>(`/oracle/decision/${encodeURIComponent(decisionId)}/evidence`);
}
